import { formatIcelandicNumber } from "@/utils/constants";
import { normalizeFinancingSources } from "@/utils/afsol";

/* ─── colour & style tokens ─── */
export const NAVY = "#1a2b5e";

export const PAGE = {
  width: "190mm",
  minHeight: "277mm",
  margin: "0 auto",
  padding: "8mm 0 6mm",
  background: "#fff",
  color: "#111",
  fontFamily: "Arial, Helvetica, sans-serif",
  fontSize: "9.5px",
  lineHeight: 1.4,
};

export const TITLE = {
  fontSize: "18px",
  fontWeight: 700,
  textTransform: "uppercase",
  color: NAVY,
  textAlign: "center",
  margin: "0 0 6px 0",
  letterSpacing: "0.4px",
};

export const HR = { border: "none", borderTop: `1.5px solid ${NAVY}`, margin: "5px 0" };
export const HR_LIGHT = { border: "none", borderTop: "0.7px solid #b0bec5", margin: "4px 0" };

export const SH = {
  fontSize: "10px",
  fontWeight: 700,
  textTransform: "uppercase",
  color: NAVY,
  margin: 0,
};

/* ─── helpers ─── */
export const fmtDate = (v) => {
  if (!v) return "";
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(v)) return v;
  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(v)) {
    const [d, m, y] = v.split('.');
    return `${d.padStart(2,'0')}.${m.padStart(2,'0')}.${y}`;
  }
  try {
    const d = new Date(v.includes("T") ? v : `${v}T00:00:00`);
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
  } catch { return v; }
};

export const coOwnerLabel = (extraType) => {
  if (extraType === "fh") return "FYRIR HÖND FÉLAGS";
  if (extraType === "eu") return "EFTIR UMBOÐ";
  if (extraType === "umradamadur") return "UMRÁÐAMAÐUR";
  return "MEÐEIGANDI";
};

export const fmtPrice = (v) => {
  if (v == null || v === "") return "";
  const n = Number(String(v).replace(/\./g, "").replace(/,/g, "."));
  return Number.isFinite(n) ? `${formatIcelandicNumber(n)} kr.` : String(v);
};

export const chk = (v) => (v ? "☑" : "☐");

export const hasParty = (p) => p && (p.kennitala || p.name || p.address || p.phone || p.email);

/* ─── Icelandic number to words (for legal amounts) ─── */
export const numberToIcelandicWords = (num) => {
  if (!num || num <= 0) return "";
  num = Math.round(Math.abs(Number(String(num).replace(/\./g, "").replace(/,/g, "."))));
  if (!Number.isFinite(num) || num === 0) return "";

  const onesN = ["", "eitt", "tvö", "þrjú", "fjögur", "fimm", "sex", "sjö", "átta", "níu"];
  const onesF = ["", "ein", "tvær", "þrjár", "fjórar", "fimm", "sex", "sjö", "átta", "níu"];
  const teens = ["tíu", "ellefu", "tólf", "þrettán", "fjórtán", "fimmtán", "sextán", "sautján", "átján", "nítján"];
  const tensW = ["", "", "tuttugu", "þrjátíu", "fjörutíu", "fimmtíu", "sextíu", "sjötíu", "áttatíu", "níutíu"];

  const below100 = (n, fem) => {
    if (n === 0) return "";
    const o = fem ? onesF : onesN;
    if (n < 10) return o[n];
    if (n < 20) return teens[n - 10];
    const t = Math.floor(n / 10), u = n % 10;
    return u > 0 ? tensW[t] + "og" + o[u] : tensW[t];
  };

  const below1000 = (n, fem = false) => {
    if (n === 0) return "";
    const o = fem ? onesF : onesN;
    const h = Math.floor(n / 100), rem = n % 100;
    const t = Math.floor(rem / 10), u = rem % 10;
    let r = "";
    if (h > 0) r = o[h] + (h === 1 ? "hundrað" : "hundruð");
    if (rem > 0) {
      if (rem >= 10 && rem < 20) {
        r += (h > 0 ? "og" : "") + teens[rem - 10];
      } else if (t >= 2 && u > 0) {
        r += tensW[t] + "og" + (fem ? onesF : onesN)[u];
      } else if (t >= 2) {
        r += (h > 0 ? "og" : "") + tensW[t];
      } else {
        r += (h > 0 ? "og" : "") + (fem ? onesF : onesN)[u];
      }
    }
    return r;
  };

  let result = "";
  const millions = Math.floor(num / 1000000);
  const thousands = Math.floor((num % 1000000) / 1000);
  const units = num % 1000;

  if (millions > 0) {
    if (millions === 1) result += "einmilljón";
    else result += below1000(millions, true) + "milljónir";
  }
  if (thousands > 0) result += below1000(thousands, false) + "þúsund";
  if (units > 0) {
    if (result) result += "og";
    result += below1000(units, true);
  }

  return result + "krónur 00/100-";
};

export const payLines = (pmt = {}) =>
  (pmt.breakdown || []).filter((i) => i?.amount).map((i) => `${i.label}: ${fmtPrice(i.amount)}`);

export const finLines = (items) => normalizeFinancingSources(items);

export const attachList = (att = {}) => {
  const map = [
    ["registrationCertificate", "Skráningarskírteini"],
    ["inspectionReport", "Ferilskrá"],
    ["serviceBook", "Þjónustubók"],
    ["ownerManual", "Eigendahandbók"],
    ["skiBox", "Skoðunarvottorð"],
    ["debtAndInsuranceCertificate", "Skulda- & vátryggingastaða"],
    ["lubricationBook", "Smurbók"],
  ];
  return map.filter(([k]) => att[k]).map(([, l]) => l);
};

/* ─── shared sub-components ─── */
export const PartyBlock = ({ title, party, insurer, showInsurer = true }) => (
  <div style={{ marginBottom: "2px" }}>
    <div style={SH}>{title}</div>
    <div style={{ marginTop: "1px" }}>{party?.name || ""}</div>
    <div>Kennitala {party?.kennitala || ""}</div>
    <div>{party?.address || ""}</div>
    <div>{[party?.postalCode, party?.city].filter(Boolean).join(" ")}</div>
    <div>Sími {party?.phone || ""}</div>
    <div>{party?.email || ""}</div>
    {showInsurer && <div>Tryggingafélag {insurer || ""}</div>}
  </div>
);

export const Footer = ({ createdBy, refNum }) => (
  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "9px", marginTop: "6px" }}>
    <span>Sölumaður: {createdBy || ""}</span>
    <span>Afsal númer {refNum || ""}</span>
  </div>
);

export const SectionTitle = ({ children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
    <span style={{ ...SH, fontStyle: "italic", whiteSpace: "nowrap" }}>{children}</span>
    <div style={{ flex: 1, borderTop: "1.5px solid #999", marginTop: "2px" }} />
  </div>
);
